import { type Opportunity, type Population } from "@entities";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Typography,
} from "@mui/material";
import {
  OPPORTUNITY_TITLE_MAX_LENGTH,
  VOLUNTEER_DESCRIPTION_MAX_LENGTH,
} from "@shared/helpers/constants";
import {
  OPPORTUNITY_VOLUNTEER_MODE_HEB,
  resolveOpportunityVolunteerMode,
} from "@shared/helpers/opportunity-volunteer-mode";
import { linko, type MethodBody, type MethodResponse } from "@shared/linko";
import { useEffect, useMemo, useState } from "react";
import { flushSync } from "react-dom";
import { useOrganization } from "../../contexts/OrganizationProvider";
import { useRequest } from "../../contexts/RequestProvider";
import { useForm } from "../../helpers/forms/FormProvider";
import { useField, useFormikContext } from "formik";

const MIN_DESCRIPTION_LENGTH = 5;

export type Suggestion = NonNullable<
  MethodResponse<typeof linko.opportunity.queries.getAidRequestFormat>["result"]
>;

export type FormatPayload = MethodBody<
  typeof linko.opportunity.queries.startAidRequestFormat
>;

type ApplySelection = {
  title: boolean;
  description: boolean;
  population: boolean;
  isRemote: boolean;
  volunteerMode: boolean;
  isLongTerm: boolean;
  timeDescription: boolean;
};

type Props = {
  open: boolean;
  suggestion: Suggestion | null;
  onClose: () => void;
  onRetry: () => void;
};

export function buildFormatPayload(
  organizationId: string,
  values: Partial<Opportunity>,
  currentEvent?: Partial<Opportunity> | null,
): FormatPayload {
  const partnerOrganizationId =
    String(
      values.partnerOrganizationId ??
        currentEvent?.partnerOrganizationId ??
        "",
    ).trim() || undefined;
  return {
    organizationId,
    rawDescription: String(values.description ?? "").trim(),
    rawTitle: String(values.title ?? "").trim() || undefined,
    internalComments: String(values.comments ?? "").trim() || undefined,
    partnerOrganizationId,
  };
}

export function validatePayload(payload: FormatPayload): string | null {
  if (payload.rawDescription.length < MIN_DESCRIPTION_LENGTH) {
    return `יש להזין תיאור של לפחות ${MIN_DESCRIPTION_LENGTH} תווים לפני פרמוט AI`;
  }
  return null;
}

function formatSelectedPopulationNames(
  selected: (Population | { id: string; } | string)[] | undefined,
  populations: Population[],
): string {
  return (selected ?? [])
    .map((item) => {
      if (typeof item === "string") {
        return populations.find((p) => p.id === item)?.name ?? item;
      }
      if ("name" in item && typeof item.name === "string") {
        return item.name;
      }
      return populations.find((p) => p.id === item.id)?.name ?? item.id;
    })
    .filter(Boolean)
    .join(", ");
}

export function AidRequestAiFormatDialog({
  open,
  suggestion,
  onClose,
  onRetry,
}: Props) {
  const { currOrganization } = useOrganization();
  const { currentEvent, setCurrentEvent, markAiAppliedNow } = useRequest();
  const { values, setValues, setTouched } = useForm<Partial<Opportunity>>();
  const { setFieldValue, setFieldTouched, setValues: setFormikValues } =
    useFormikContext<Partial<Opportunity>>();
  const [, , titleHelpers] = useField<string>("title");
  const [, , descriptionHelpers] = useField<string>("description");

  const [apply, setApply] = useState<ApplySelection>({
    title: true,
    description: true,
    population: true,
    isRemote: true,
    volunteerMode: true,
    isLongTerm: true,
    timeDescription: true,
  });

  const populations = useMemo(
    () => (currOrganization?.populations ?? []) as Population[],
    [currOrganization?.populations],
  );

  const hasPopulations = populations.length > 0;

  useEffect(() => {
    if (!open || !suggestion) return;
    setApply({
      title: true,
      description: true,
      population: suggestion.populationResolved,
      isRemote: true,
      volunteerMode: true,
      isLongTerm: true,
      timeDescription: !!suggestion.suggestedTimeDescription,
    });
  }, [open, suggestion]);

  const handleClose = () => {
    onClose();
  };

  const handleRetry = () => {
    onRetry();
  };

  const handleApply = () => {
    if (!suggestion) return;

    // Mark before applying values: prevents any immediate re-hydration from wiping the changes.
    markAiAppliedNow();

    // Defensive: some environments still end up with title being overwritten in the same tick
    // (e.g. submit side-effects / re-hydration). We'll re-apply title in a microtask.
    const titleToReapply = apply.title
      ? suggestion.suggestedTitle.slice(0, OPPORTUNITY_TITLE_MAX_LENGTH)
      : null;

    // Ensure title/description commit before dialog closes (prevents rare races with
    // form re-hydration / focus-loss logic).
    flushSync(() => {
      if (apply.title) {
        const title = suggestion.suggestedTitle.slice(
          0,
          OPPORTUNITY_TITLE_MAX_LENGTH,
        );
        setCurrentEvent((prev) => ({ ...prev, title }));
        // Most robust path: update via the same useField mechanism as FormTextField.
        titleHelpers.setValue(title, false);
        titleHelpers.setTouched(true, false);
        setFieldValue("title", title, false);
        setFieldTouched("title", true, false);
        setFormikValues((prev) => ({ ...prev, title }), false);
        setValues((draft) => {
          (draft as any).title = title;
        });
        setTouched((draft) => {
          (draft as any).title = true;
        });
      }

      if (apply.description) {
        const description = suggestion.suggestedDescription.slice(
          0,
          VOLUNTEER_DESCRIPTION_MAX_LENGTH,
        );
        setCurrentEvent((prev) => ({ ...prev, description }));
        descriptionHelpers.setValue(description, false);
        descriptionHelpers.setTouched(true, false);
        setFieldValue("description", description, false);
        setFieldTouched("description", true, false);
        setFormikValues((prev) => ({ ...prev, description }), false);
        setValues((draft) => {
          (draft as any).description = description;
        });
        setTouched((draft) => {
          (draft as any).description = true;
        });
      }
    });

    if (titleToReapply != null) {
      queueMicrotask(() => {
        titleHelpers.setValue(titleToReapply, false);
        setFieldValue("title", titleToReapply, false);
        setFormikValues((prev) => ({ ...prev, title: titleToReapply }), false);
      });
    }

    if (apply.population && suggestion.populationId) {
      const population = populations.find(
        (p) => p.id === suggestion.populationId,
      );
      if (population) {
        setCurrentEvent((prev) => ({
          ...prev,
          selectedPopulations: [population],
        }));
        // Still keep setValues for non-Formik-managed fields in some forms.
        setValues((draft) => {
          (draft as any).selectedPopulations = [population];
        });
      }
    }

    if (apply.isRemote) {
      setCurrentEvent((prev) => ({ ...prev, isRemote: suggestion.isRemote }));
      setValues((draft) => {
        draft.isRemote = suggestion.isRemote;
      });
      setTouched((draft) => {
        (draft as any).isRemote = true;
      });
    }

    if (apply.volunteerMode) {
      setCurrentEvent((prev) => ({
        ...prev,
        volunteerMode: suggestion.volunteerMode,
      }));
      setValues((draft) => {
        draft.volunteerMode = suggestion.volunteerMode;
      });
      setTouched((draft) => {
        (draft as any).volunteerMode = true;
      });
    }

    if (apply.isLongTerm) {
      setCurrentEvent((prev) => ({
        ...prev,
        isLongTerm: suggestion.isLongTerm,
      }));
      setValues((draft) => {
        draft.isLongTerm = suggestion.isLongTerm;
      });
      setTouched((draft) => {
        (draft as any).isLongTerm = true;
      });
    }

    if (apply.timeDescription && suggestion.suggestedTimeDescription) {
      setCurrentEvent((prev) => ({
        ...prev,
        hasTime: false,
        timeDescription: suggestion.suggestedTimeDescription,
      }));
      setValues((draft) => {
        draft.hasTime = false;
        draft.timeDescription = suggestion.suggestedTimeDescription;
      });
      setTouched((draft) => {
        (draft as any).hasTime = true;
        (draft as any).timeDescription = true;
      });
    }

    handleClose();
  };

  const showPopulationSuggestion =
    hasPopulations && (suggestion?.suggestedTargetPopulationName ?? "").length > 0;

  const formatBool = (value: boolean) => (value ? "כן" : "לא");

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth dir="rtl">
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <AutoAwesomeIcon color="primary" />
        הצעות פרמוט AI
      </DialogTitle>
      <DialogContent>
        {suggestion && (
          <Stack gap={2}>
            {!hasPopulations && (
              <Alert severity="info">
                לא הוגדרו אוכלוסיות יעד לארגון - רק כותרת ותיאור יוצעו.
              </Alert>
            )}
            {hasPopulations &&
              !suggestion.populationResolved &&
              suggestion.suggestedTargetPopulationName && (
                <Alert severity="warning">
                  אוכלוסיית היעד שהמודל הציע (
                  {suggestion.suggestedTargetPopulationName}) לא התאימה לרשימה.
                </Alert>
              )}

            <SuggestionBlock
              label="כותרת"
              current={String(values.title ?? "")}
              suggested={suggestion.suggestedTitle}
              checked={apply.title}
              onCheckedChange={(checked) =>
                setApply((prev) => ({ ...prev, title: checked }))
              }
            />
            <SuggestionBlock
              label="תיאור"
              current={String(values.description ?? "")}
              suggested={suggestion.suggestedDescription}
              checked={apply.description}
              onCheckedChange={(checked) =>
                setApply((prev) => ({ ...prev, description: checked }))
              }
              multiline
            />
            {showPopulationSuggestion && (
              <SuggestionBlock
                label="אוכלוסיית יעד"
                current={
                  formatSelectedPopulationNames(
                    currentEvent.selectedPopulations,
                    populations,
                  ) || "-"
                }
                suggested={suggestion.suggestedTargetPopulationName}
                checked={apply.population}
                disabled={!suggestion.populationResolved}
                onCheckedChange={(checked) =>
                  setApply((prev) => ({ ...prev, population: checked }))
                }
              />
            )}
            <SuggestionBlock
              label="משימה מרחוק"
              current={formatBool(!!values.isRemote)}
              suggested={formatBool(suggestion.isRemote)}
              checked={apply.isRemote}
              onCheckedChange={(checked) =>
                setApply((prev) => ({ ...prev, isRemote: checked }))
              }
            />
            <SuggestionBlock
              label="סוג מתנדבים"
              current={
                OPPORTUNITY_VOLUNTEER_MODE_HEB[
                  resolveOpportunityVolunteerMode(currentEvent.volunteerMode)
                ]
              }
              suggested={
                OPPORTUNITY_VOLUNTEER_MODE_HEB[
                  resolveOpportunityVolunteerMode(suggestion.volunteerMode)
                ]
              }
              checked={apply.volunteerMode}
              onCheckedChange={(checked) =>
                setApply((prev) => ({ ...prev, volunteerMode: checked }))
              }
            />
            <SuggestionBlock
              label="התנדבות ממושכת"
              current={formatBool(!!currentEvent.isLongTerm)}
              suggested={formatBool(suggestion.isLongTerm)}
              checked={apply.isLongTerm}
              onCheckedChange={(checked) =>
                setApply((prev) => ({ ...prev, isLongTerm: checked }))
              }
            />
            {suggestion.suggestedTimeDescription && (
              <SuggestionBlock
                label="תיאור זמן"
                current={String(currentEvent.timeDescription ?? "") || "-"}
                suggested={suggestion.suggestedTimeDescription}
                checked={apply.timeDescription}
                onCheckedChange={(checked) =>
                  setApply((prev) => ({ ...prev, timeDescription: checked }))
                }
              />
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button onClick={handleClose} variant="outlined">
          ביטול
        </Button>
        {suggestion && (
          <Button variant="text" onClick={handleRetry}>
            ניסיון חוזר
          </Button>
        )}
        {suggestion && (
          <Button
            variant="contained"
            type="button"
            onClick={handleApply}
            disabled={!Object.values(apply).some(Boolean)}
          >
            החלת נבחרים
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

function SuggestionBlock({
  label,
  current,
  suggested,
  checked,
  disabled,
  multiline,
  onCheckedChange,
}: {
  label: string;
  current: string;
  suggested: string;
  checked: boolean;
  disabled?: boolean;
  multiline?: boolean;
  onCheckedChange: (checked: boolean) => void;
}) {
  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        p: 2,
      }}
    >
      <FormControlLabel
        control={
          <Checkbox
            checked={checked}
            disabled={disabled}
            onChange={(e) => onCheckedChange(e.target.checked)}
          />
        }
        label={
          <Typography fontWeight={700} color="primary">
            {label}
          </Typography>
        }
      />
      <Stack direction={{ xs: "column", sm: "row" }} gap={2} mt={1}>
        <Box flex={1} minWidth={0}>
          <Typography variant="caption" color="text.secondary">
            נוכחי
          </Typography>
          <Typography
            variant="body2"
            sx={{
              whiteSpace: multiline ? "pre-wrap" : "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {current || "-"}
          </Typography>
        </Box>
        <Box flex={1} minWidth={0}>
          <Typography variant="caption" color="text.secondary">
            מוצע
          </Typography>
          <Typography
            variant="body2"
            sx={{ whiteSpace: multiline ? "pre-wrap" : "normal" }}
          >
            {suggested}
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
}
